<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { getCompanyOutstandingInvoices, type OutstandingInvoice } from '@/api/commercialDashboardApi'
import { formatDate } from './dashboardFormat'

const props = defineProps<{
  company: { uuid: string; name: string } | null
}>()

const emit = defineEmits<{ close: [] }>()

const invoices = ref<OutstandingInvoice[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

const total = computed(() => invoices.value.reduce((sum, inv) => sum + (inv.amountResidual ?? 0), 0))

watch(
  () => props.company,
  async (company) => {
    if (!company) return
    loading.value = true
    error.value = null
    invoices.value = []
    try {
      invoices.value = await getCompanyOutstandingInvoices(company.uuid)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des factures'
    } finally {
      loading.value = false
    }
  }
)

const preciseAmountFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
function formatAmountPrecise(amount: number | null): string {
  return preciseAmountFormatter.format(amount ?? 0)
}

const PAYMENT_STATE_LABELS: Record<string, string> = {
  not_paid: 'Non payée',
  partial: 'Partiellement payée',
  in_payment: 'En paiement',
  reversed: 'Extournée',
}
function paymentStateLabel(state: string | null): string {
  if (!state) return '—'
  return PAYMENT_STATE_LABELS[state] ?? state
}
</script>

<template>
  <div v-if="company" class="modal modal-open">
    <div class="modal-box max-w-3xl flex flex-col max-h-[80vh] p-0">
      <!-- En-tête épinglé -->
      <div class="flex items-start justify-between gap-4 shrink-0 p-5 pb-3 border-b border-base-200">
        <div>
          <h3 class="text-lg font-bold">Factures impayées</h3>
          <p class="text-sm opacity-60">
            {{ company.name }}
            <span v-if="!loading && invoices.length > 0">· {{ invoices.length }} facture{{ invoices.length > 1 ? 's' : '' }}</span>
          </p>
        </div>
        <button class="btn btn-sm btn-circle btn-ghost" @click="emit('close')">✕</button>
      </div>

      <div v-if="loading" class="flex justify-center py-10">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>

      <div v-else-if="error" class="alert alert-error m-5">
        <span class="text-sm">{{ error }}</span>
      </div>

      <div v-else-if="invoices.length === 0" class="py-10 text-center text-sm opacity-60">
        Aucune facture impayée.
      </div>

      <!-- Zone défilante : seule la liste scrolle -->
      <div v-else class="flex-1 overflow-auto px-5">
        <table class="table table-sm">
          <thead class="sticky top-0 bg-base-100 z-10">
            <tr>
              <th>Facture</th>
              <th>Date</th>
              <th>Échéance</th>
              <th class="text-right">Total</th>
              <th class="text-right">Restant dû</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inv in invoices" :key="inv.uuid">
              <td class="font-medium">{{ inv.name ?? '—' }}</td>
              <td class="text-sm whitespace-nowrap">{{ inv.invoiceDate ? formatDate(inv.invoiceDate) : '—' }}</td>
              <td class="text-sm whitespace-nowrap">
                <span :class="inv.isOverdue ? 'text-error font-semibold' : ''">
                  {{ inv.invoiceDateDue ? formatDate(inv.invoiceDateDue) : '—' }}
                </span>
              </td>
              <td class="text-right text-sm whitespace-nowrap">{{ formatAmountPrecise(inv.amountTotal) }}</td>
              <td class="text-right text-sm font-semibold text-error whitespace-nowrap">{{ formatAmountPrecise(inv.amountResidual) }}</td>
              <td>
                <div class="flex flex-col gap-0.5">
                  <span class="text-xs">{{ paymentStateLabel(inv.paymentState) }}</span>
                  <span v-if="inv.isOverdue" class="badge badge-error badge-xs">En retard</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pied épinglé : total + fermer -->
      <div class="flex items-center justify-between gap-4 shrink-0 p-5 pt-3 border-t border-base-200">
        <div class="text-sm">
          <span class="opacity-60">Total impayé&nbsp;:</span>
          <span class="ml-1 font-bold text-error">{{ formatAmountPrecise(total) }}</span>
        </div>
        <button class="btn btn-sm" @click="emit('close')">Fermer</button>
      </div>
    </div>
    <div class="modal-backdrop bg-black/40" @click="emit('close')"></div>
  </div>
</template>
